// src/pages/Checkout.js
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';

// ---------- UI ----------
const Page = styled.div`max-width:800px;margin:40px auto;padding:0 20px;`;
const Header = styled.h2`color:#e1d6c0;margin-bottom:12px;`;
const Section = styled.div`background:rgba(13,13,13,.8);padding:16px;border-radius:12px;margin-bottom:16px;border:1px solid #333;`;
const Label = styled.label`display:block;color:#e1d6c0;margin-bottom:6px;font-size:.95rem;`;
const Input = styled.input`width:100%;padding:10px;background:#111;border:1px solid #333;border-radius:8px;color:#fff;margin-bottom:12px;`;
const Textarea = styled.textarea`width:100%;padding:10px;background:#111;border:1px solid #333;border-radius:8px;color:#fff;margin-bottom:12px;min-height:90px;`;
const Select = styled.select`width:100%;padding:10px;background:#111;border:1px solid #333;border-radius:8px;color:#fff;margin-bottom:12px;`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`text-align:left;padding:10px;border-bottom:1px solid #333;color:#e1d6c0;`;
const Td = styled.td`padding:10px;border-bottom:1px dashed #333;color:#fff;`;
const Row = styled.div`display:flex;gap:12px;`;
const Half = styled.div`flex:1;`;
const Actions = styled.div`display:flex;justify-content:space-between;align-items:center;`;
const PrimaryBtn = styled.button`padding:12px 20px;font-weight:700;border-radius:24px;border:none;background:#e1d6c0;color:#0d0d0d;cursor:pointer;`;
const GhostBtn = styled.button`background:transparent;color:#e1d6c0;border:1px solid #e1d6c0;padding:10px 16px;border-radius:24px;cursor:pointer;`;
const ErrorText = styled.div`color:#ff8a8a;margin:8px 0 0;`;
const RadioRow = styled.div`display:flex;gap:16px;margin:8px 0 12px; align-items:center; color:#e1d6c0;`;

const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

// ---------- Time utils ----------
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const makeDateTodayAt = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m || 0, 0, 0);
};
const roundUpToNextQuarter = (date) => {
  const d = new Date(date.getTime());
  const minutes = d.getMinutes();
  const remainder = minutes % 15;
  if (remainder) d.setMinutes(minutes + (15 - remainder), 0, 0);
  else if (d.getSeconds() > 0 || d.getMilliseconds() > 0) d.setMinutes(minutes + 15, 0, 0);
  else d.setSeconds(0, 0);
  return d;
};
const fmtTimeLabel = (date) => {
  const h = date.getHours();
  const m = pad2(date.getMinutes());
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${m} ${suffix}`;
};
const addMinutes = (d, min) => new Date(d.getTime() + min * 60 * 1000);

// First slot after now + 30m, last slot ≤ close-60m
const buildTodaySlots = (windows) => {
  if (!Array.isArray(windows) || windows.length === 0) return [];
  const startFrom = roundUpToNextQuarter(addMinutes(new Date(), 30));

  const slots = [];
  for (const w of windows) {
    if (!w?.start || !w?.end) continue;
    const winStart = makeDateTodayAt(w.start);
    const winEnd   = makeDateTodayAt(w.end);
    let t = roundUpToNextQuarter(new Date(Math.max(winStart.getTime(), startFrom.getTime())));
    const endCap = addMinutes(winEnd, -60);
    if (endCap <= winStart) continue;
    while (t <= endCap) {
      slots.push(new Date(t.getTime()));
      t = addMinutes(t, 15);
    }
  }
  const map = new Map();
  for (const d of slots) map.set(d.getTime(), d);
  return Array.from(map.values()).sort((a, b) => a - b);
};

// Are we open *right now* (any window where now >= open && now < close)?
const isOpenNow = (windows) => {
  if (!Array.isArray(windows) || !windows.length) return false;
  const now = new Date();
  for (const w of windows) {
    if (!w?.start || !w?.end) continue;
    const open = makeDateTodayAt(w.start);
    const close = makeDateTodayAt(w.end);
    if (now >= open && now < close) return true;
  }
  return false;
};

// ---------- Component ----------
export default function Checkout() {
  const { items, totalCents, clearCart } = useCart();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [special, setSpecial] = useState('');

  // 'now' | 'later'
  const [pickupMode, setPickupMode] = useState('later');
  const [pickupIso, setPickupIso] = useState('');

  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState({ date: '', windows: [] }); // {date:'YYYY-MM-DD', windows:[{start,end}]}

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErr('');
      try {
        const r = await fetch('/api/store-hours/today');
        if (!r.ok) throw new Error('Failed to load store hours');
        const data = await r.json();
        if (!cancelled) setHours({ date: data.date, windows: data.windows || [] });
      } catch {
        if (!cancelled) setErr('Unable to load store hours. Please try again or call the store.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const slots = useMemo(() => buildTodaySlots(hours.windows), [hours.windows]);
  const openNow = useMemo(() => isOpenNow(hours.windows), [hours.windows]);

  // Default mode: if open now → 'now', else 'later'
  useEffect(() => {
    if (openNow) setPickupMode('now');
    else setPickupMode('later');
  }, [openNow]);

  // Default slot when in 'later' mode
  useEffect(() => {
    if (pickupMode !== 'later') return;
    if (!slots.length) { setPickupIso(''); return; }
    setPickupIso(slots[0].toISOString());
  }, [pickupMode, slots.length]);

  const valid = () => {
    if (!items.length) return setErr('Your cart is empty.'), false;
    if (!name.trim()) return setErr('Please enter your name.'), false;
    if (!email.trim()) return setErr('Please enter your email.'), false;
    if (!phone.trim()) return setErr('Please enter your phone.'), false;
    if (pickupMode === 'later' && !pickupIso) return setErr('Please select a pickup time.'), false;
    if (pickupMode === 'now' && !openNow) return setErr('Store is not open right now.'), false;
    setErr(''); return true;
  };

  const submitOrder = async () => {
    if (!valid()) return;

    const now = new Date();
    const pickupAtLocal = pickupMode === 'now' ? now : new Date(pickupIso);

    const payload = {
      customer: { name, email, phone },
      specialRequests: special,
      pickupAt: pickupAtLocal.toISOString(),
      items: items.map(i => ({ id: i.id, name: i.name, quantity: i.quantity, unit_amount: i.unitAmount })),
      totalCents
    };

    try {
      setLoading(true);
      const res = await fetch('/api/orders/new', {  // <-- fixed path (plural)
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErr(data?.message || 'Failed to place order.');
        setLoading(false);
        return;
      }

      clearCart();
      navigate('/order/success');
    } catch {
      setErr('Network error while placing order.');
      setLoading(false);
    }
  };

  const placeDisabled =
    loading ||
    !items.length ||
    (!openNow && pickupMode === 'now') ||
    (pickupMode === 'later' && !slots.length);

  return (
    <Page>
      <Header>Checkout</Header>

      <Section>
        <h3 style={{ color:'#e1d6c0', marginTop:0 }}>Your Items</h3>
        {items.length === 0 ? (
          <div style={{ color:'#fff' }}>
            Your cart is empty. <Link to="/menu">Browse the Menu</Link>
          </div>
        ) : (
          <>
            <Table>
              <thead>
                <tr><Th>Item</Th><Th>Qty</Th><Th>Total</Th></tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id}>
                    <Td>{i.name}</Td>
                    <Td>{i.quantity}</Td>
                    <Td>{formatCents(i.unitAmount * i.quantity)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div style={{ textAlign:'right', color:'#e1d6c0', marginTop:10 }}>
              <strong>Order Total: {formatCents(totalCents)}</strong>
            </div>
          </>
        )}
      </Section>

      <Section>
        <h3 style={{ color:'#e1d6c0', marginTop:0 }}>Contact</h3>
        <Row>
          <Half>
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e)=>setName(e.target.value)} />
          </Half>
          <Half>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={phone} onChange={(e)=>setPhone(e.target.value)} />
          </Half>
        </Row>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} />
      </Section>

      <Section>
        <h3 style={{ color:'#e1d6c0', marginTop:0 }}>Pickup</h3>

        <RadioRow>
          <label>
            <input
              type="radio"
              name="pickupMode"
              value="now"
              checked={pickupMode === 'now'}
              onChange={()=>setPickupMode('now')}
              disabled={!openNow}
              style={{ marginRight:8 }}
            />
            Now {openNow ? '' : '(unavailable)'}
          </label>

          <label>
            <input
              type="radio"
              name="pickupMode"
              value="later"
              checked={pickupMode === 'later'}
              onChange={()=>setPickupMode('later')}
              style={{ marginRight:8, marginLeft:12 }}
            />
            Schedule
          </label>
        </RadioRow>

        {pickupMode === 'later' && (
          slots.length ? (
            <>
              <Label htmlFor="pickup">Choose a time (today)</Label>
              <Select
                id="pickup"
                value={pickupIso}
                onChange={(e)=>setPickupIso(e.target.value)}
                disabled={loading || !slots.length}
              >
                {slots.map(d => (
                  <option key={d.toISOString()} value={d.toISOString()}>
                    {fmtTimeLabel(d)}
                  </option>
                ))}
              </Select>
            </>
          ) : (
            <div style={{ color:'#fff' }}>No future pickup times left today.</div>
          )
        )}

        {pickupMode === 'now' && !openNow && (
          <div style={{ color:'#fff' }}>Store isn’t open right now.</div>
        )}
      </Section>

      <Section>
        <h3 style={{ color:'#e1d6c0', marginTop:0 }}>Special Requests</h3>
        <Textarea
          placeholder="Allergies, extra sauce, instructions…"
          value={special}
          onChange={(e)=>setSpecial(e.target.value)}
        />
      </Section>

      {err ? <ErrorText>{err}</ErrorText> : null}

      <Actions>
        <Link to="/order"><GhostBtn>← Back to Order</GhostBtn></Link>
        <PrimaryBtn onClick={submitOrder} disabled={placeDisabled}>
          {placeDisabled ? (loading ? 'Submitting…' : 'Unavailable') : 'Place Order'}
        </PrimaryBtn>
      </Actions>
    </Page>
  );
}
